/* Account-workspace local helpers. Inlined here (not shared) so this page owns
   its derivations without touching src/lib/**. */
import type {
  Dataset, Account, Property, ID, ActionItem, KeyDate, RelationshipTrack,
  Contact, Opportunity, ScheduledEvent, Project, WorkOrder, FinancialPeriod,
  Activity, AccountInsight,
} from '@/types/entities'
import { latest, noi, financialPeriodsForProperty } from '@/lib/selectors'

/** Shape produced by AccountWorkspace and threaded into every tab. */
export interface AccountDerived {
  tracks: RelationshipTrack[]
  properties: Property[]
  contacts: Contact[]
  opportunities: Opportunity[]
  openItems: ActionItem[]
  propIds: Set<ID>
  events: ScheduledEvent[]
  projects: Project[]
  workOrders: WorkOrder[]
  financialPeriods: FinancialPeriod[]
  activities: Activity[]
  insight?: AccountInsight
  revenue?: number
  noiActual?: number
  stabilized: number
}

/** Latest in-place revenue snapshot for a property (monthly run-rate). */
export const latestRevenue = (p: Property): number | undefined => latest(p.revenue)
/** Latest occupancy snapshot. */
export const latestOcc = (p: Property): number | undefined => latest(p.occupancyPct)

/** Sum of latest in-place revenue across a set of properties (undefined if none have data). */
export function sumLatestRevenue(props: Property[]): number | undefined {
  const vals = props.map(latestRevenue).filter((v): v is number => v != null)
  return vals.length ? vals.reduce((s, v) => s + v, 0) : undefined
}

/** Total NOI (actual) across all financial periods owned by these properties. */
export function sumNoiActual(d: Dataset, props: Property[]): number | undefined {
  let total = 0
  let found = false
  for (const p of props) {
    for (const fp of financialPeriodsForProperty(d, p.id)) {
      total += noi(fp, 'actual')
      found = true
    }
  }
  return found ? total : undefined
}

/** Friendly one-character/zip-style location string. */
export function locStr(p: Property): string {
  const a = p.address
  if (!a) return '—'
  const city = a.city ?? ''
  const st = a.state ?? ''
  return [city, st].filter(Boolean).join(', ') || '—'
}

/** Sparkline series of in-place revenue (chronological). */
export function revSeries(p: Property): number[] {
  return (p.revenue ?? []).slice().sort((a, b) => (a.date < b.date ? -1 : 1)).map((s) => s.value)
}
export function occSeries(p: Property): number[] {
  return (p.occupancyPct ?? []).slice().sort((a, b) => (a.date < b.date ? -1 : 1)).map((s) => s.value)
}

/** Account-scoped open action items already filtered by selectors; this adds priority sort. */
const PRIORITY_RANK: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 }
export function sortByPriority(items: ActionItem[]): ActionItem[] {
  return [...items].sort((a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9))
}

/** Key dates attached to an account or any of its properties. */
export function keyDatesForAccount(d: Dataset, accountId: ID, propIds: Set<ID>): KeyDate[] {
  return d.keyDates.filter((k) => {
    const r = k.attachedTo
    if (r.kind === 'account' && r.id === accountId) return true
    if (r.kind === 'property' && propIds.has(r.id)) return true
    return false
  })
}

/** Map a KeyDate status / urgency to a Badge tone. */
export const urgencyTone = (s?: string): 'ok' | 'warn' | 'alert' | 'neutral' | 'info' => {
  switch (s) {
    case 'Overdue': return 'alert'
    case 'Due Soon': return 'warn'
    case 'Met': return 'ok'
    case 'Waived': return 'neutral'
    default: return 'info'
  }
}

export const priorityTone = (p?: string): 'ok' | 'warn' | 'alert' | 'neutral' | 'info' => {
  switch (p) {
    case 'Critical': return 'alert'
    case 'High': return 'warn'
    case 'Medium': return 'info'
    default: return 'neutral'
  }
}

export const itemStatusTone = (s?: string): 'ok' | 'warn' | 'alert' | 'neutral' | 'info' => {
  switch (s) {
    case 'Closed': return 'ok'
    case 'Blocked': return 'alert'
    case 'In Progress': return 'info'
    default: return 'neutral'
  }
}

export const acctTypeTone = (t?: Account['type']): 'navy' | 'info' | 'neutral' => {
  switch (t) {
    case 'Investor': return 'navy'
    case 'JV': return 'navy'
    case '3PM': return 'info'
    default: return 'neutral'
  }
}
